/**
 * Zod schemas and TypeScript types for WebSocket frame protocol.
 *
 * Covers all server-to-client frame types defined in plan 03-02's
 * WebSocket protocol specification. Provides typed parse + builder
 * functions for client-to-server messages.
 *
 * Server -> Client frame types:
 *   welcome, subscribed, unsubscribed, subscription_denied,
 *   alert, heartbeat, detection, pong, error
 *
 * Client -> Server action types:
 *   subscribe, unsubscribe, ping
 */
import { z } from 'zod';

// ---------------------------------------------------------------------------
// Canonical alert_type enum (from plan 03-02 CanonicalAlertType)
// ---------------------------------------------------------------------------

/** Canonical alert type values from plan 03-02 normalization. */
export const CANONICAL_ALERT_TYPES = ['approach', 'turnaround', 'entry', 'loiter_resolved', 'other'] as const;
export type CanonicalAlertType = (typeof CANONICAL_ALERT_TYPES)[number];

// ---------------------------------------------------------------------------
// Server -> Client frames
// ---------------------------------------------------------------------------

/** Welcome frame sent on successful WS authentication. */
export const WelcomeFrameSchema = z.object({
  type: z.literal('welcome'),
  user_id: z.string(),
  org_id: z.string(),
  actor: z.string(),
  role: z.string(),
  impersonating_as: z.string().nullable(),
});

/** Subscription acknowledgment. */
export const SubscribedFrameSchema = z.object({
  type: z.literal('subscribed'),
  topic: z.string(),
});

/** Unsubscription acknowledgment. */
export const UnsubscribedFrameSchema = z.object({
  type: z.literal('unsubscribed'),
  topic: z.string(),
});

/** Subscription denied - org/permission check failed. */
export const SubscriptionDeniedFrameSchema = z.object({
  type: z.literal('subscription_denied'),
  topic: z.string(),
  reason: z.string(),
});

/** Alert event frame. Sent on both alert open and as a followup resolution signal. */
export const AlertFrameSchema = z.object({
  type: z.literal('alert'),
  payload: z.object({
    alert_id: z.number().int(),
    site_id: z.string(),
    org_id: z.string(),
    alert_type: z.string(), // canonical via plan 03-02 normalization
    severity: z.enum(['info', 'warning', 'critical']),
    zone_id: z.number().int().nullable(),
    zone_name: z.string().nullable(),
    fingerprint: z.string().nullable(),
    ts: z.string(),
    evidence_url: z.string().nullable(),
  }),
});

export type AlertFrame = z.infer<typeof AlertFrameSchema>;
export type AlertPayload = AlertFrame['payload'];

/** Heartbeat frame from a site's sensor network. */
export const HeartbeatFrameSchema = z.object({
  type: z.literal('heartbeat'),
  payload: z.object({
    site_id: z.string(),
    status: z.string(),
    sensors_online: z.number().int(),
    ts: z.string(),
  }),
});

/** Detection event frame - real-time device sighting at a zone. */
export const DetectionFrameSchema = z.object({
  type: z.literal('detection'),
  payload: z.object({
    site_id: z.string(),
    fingerprint: z.string().nullable(),
    rssi: z.number(),
    zone_id: z.number().int().nullable(),
    ts: z.string(),
  }),
});

export type DetectionFrame = z.infer<typeof DetectionFrameSchema>;
export type DetectionPayload = DetectionFrame['payload'];

/** Pong response from server. */
export const PongFrameSchema = z.object({
  type: z.literal('pong'),
});

/** Server error frame. */
export const ErrorFrameSchema = z.object({
  type: z.literal('error'),
  reason: z.string(),
});

/**
 * Union of all valid server-to-client frame types.
 * Unknown types pass through as { type: string } to allow forward compat.
 */
export const ServerFrameSchema = z.union([
  WelcomeFrameSchema,
  SubscribedFrameSchema,
  UnsubscribedFrameSchema,
  SubscriptionDeniedFrameSchema,
  AlertFrameSchema,
  HeartbeatFrameSchema,
  DetectionFrameSchema,
  PongFrameSchema,
  ErrorFrameSchema,
]);

export type ServerFrame = z.infer<typeof ServerFrameSchema>;

// ---------------------------------------------------------------------------
// Frame parser
// ---------------------------------------------------------------------------

/**
 * Attempts to parse a raw WebSocket message string into a typed ServerFrame.
 *
 * On parse failure (invalid JSON or schema mismatch): returns a synthetic
 * error frame with the parse error message. Never throws.
 *
 * @param raw - Raw string from WebSocket.onmessage event.data
 * @returns Typed ServerFrame or a synthetic error frame
 */
export function parseServerFrame(raw: string): ServerFrame {
  try {
    const json: unknown = JSON.parse(raw);
    const result = ServerFrameSchema.safeParse(json);
    if (result.success) return result.data;
    // Unknown type: pass through as-is (forward compatibility)
    if (typeof json === 'object' && json !== null && 'type' in json) {
      // Silently log and return a best-effort passthrough
      console.debug('[WebSocket] Unknown frame type, passing through:', (json as { type: unknown }).type);
      return json as ServerFrame;
    }
    return { type: 'error', reason: 'schema_mismatch: ' + result.error.message };
  } catch (e) {
    return { type: 'error', reason: 'json_parse_error: ' + String(e) };
  }
}

// ---------------------------------------------------------------------------
// Client -> Server message builders
// ---------------------------------------------------------------------------

/** Client message sent to subscribe to a topic. */
export function buildSubscribeMsg(topic: string): string {
  return JSON.stringify({ action: 'subscribe', topic });
}

/** Client message sent to unsubscribe from a topic. */
export function buildUnsubscribeMsg(topic: string): string {
  return JSON.stringify({ action: 'unsubscribe', topic });
}

/** Heartbeat ping sent by the client on a 30s interval. */
export function buildPingMsg(): string {
  return JSON.stringify({ action: 'ping' });
}

// ---------------------------------------------------------------------------
// Subscription handle type
// ---------------------------------------------------------------------------

/** Handle returned by subscribe(); used for cleanup in useEffect returns. */
export interface SubscriptionHandle {
  topic: string;
  ok: boolean;
}
